import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import Slider from 'react-slick'
import { ReactComponent as IconNext } from 'components/Icons/IconNext.svg'
import { ReactComponent as IconPrev } from 'components/Icons/IconPrev.svg'
import TopBannerSliderItem from './TopBannerSliderItem'
import { getPartnerId, getUA } from 'core/singleton'
import { useTranslation } from 'react-i18next'
import { layoutCustom, layoutSmall } from 'core/util/default'

// BannerSlider Element props
interface TopBannerSliderProps {
  list?: GetTopBannerItem[]
  isBridge?: boolean
  handleClickBanner: (item: GetTopBannerItem) => boolean
}

interface SliderArrowProps {
  className?: string
  style?: object
  onClick?: () => void
}

function NextArrow(props: SliderArrowProps) {
  const { className, style, onClick } = props
  const { t } = useTranslation()
  return (
    <div className={className} style={style}>
      <button onClick={onClick}>
        {t('next_slide')}
        <IconNext />
      </button>
    </div>
  )
}

function PrevArrow(props: SliderArrowProps) {
  const { className, style, onClick } = props
  const { t } = useTranslation()
  return (
    <div className={className} style={style}>
      <button onClick={onClick}>
        {t('prev_slide')}
        <IconPrev />
      </button>
    </div>
  )
}

const TopBannerSlider: React.FC<TopBannerSliderProps> = (props) => {
  const { t } = useTranslation()
  const { list, isBridge, handleClickBanner } = props
  const [clickable, setClickable] = useState(true)
  const sliderRef = useRef<Slider>(null)
  const partnerId = getPartnerId()
  const { isSDKIOS } = getUA()

  const useArrowOnResponsive = useMemo(
    () =>
      partnerId === 'lotteimall' || partnerId === 'shinsegaelive' || partnerId === 'ldfs'
        ? true
        : false,
    [partnerId],
  )
  const useSwipeOnResponsive = useMemo(
    () => (partnerId === 'shinsegaelive' ? true : !useArrowOnResponsive),
    [partnerId, useArrowOnResponsive],
  )

  const sliderChange = (clickable: boolean) => {
    document.querySelectorAll('.slider-dots-list button').forEach(($Button) => {
      $Button.removeAttribute('title')
    })
    document.querySelector('.slick-active button')?.setAttribute('title', t('active_now'))
    setClickable(clickable)
  }

  const customPaging = (i: number) => (
    <div className="slider-dot">
      <button {...(i === 0 && { title: t('active_now') })}>{i + 1}</button>
    </div>
  )

  const settings = {
    dots: true,
    swipe: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 300,
    // eslint-disable-next-line react/display-name
    appendDots: (dots: Element) => (
      <div>
        <ul className="slider-dots-list">{dots}</ul>
      </div>
    ),
    // eslint-disable-next-line react/display-name
    customPaging,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          arrows: useArrowOnResponsive,
          swipe: useSwipeOnResponsive,
        },
      },
    ],
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    beforeChange: (currentSlide: number) => sliderChange(false),
    afterChange: (currentSlide: number) => sliderChange(true),
  }

  useEffect(() => {
    document.querySelectorAll('.slick-cloned .slide-wrap').forEach((SlideEl) => {
      SlideEl.setAttribute('tabindex', '-1')
    })
  }, [])

  if (!list || list.length === 0) return null
  return (
    <StyledTopBannerSlider {...settings} ref={sliderRef} isSDKIOS={isSDKIOS}>
      {list.map((item, i) => (
        <TopBannerSliderItem
          key={item.topBannerId}
          item={item}
          index={i}
          totalCount={list.length}
          onClickBanner={handleClickBanner}
          clickable={clickable}
          isBridge={!!isBridge}
        />
      ))}
    </StyledTopBannerSlider>
  )
}

export default TopBannerSlider

const StyledTopBannerSlider = styled(Slider)<{ isSDKIOS: boolean }>`
  padding-bottom: 20px;
  ${({ isSDKIOS }) =>
    isSDKIOS &&
    css`
      padding: 0px;
    `}
  ${({ isSDKIOS }) =>
    !isSDKIOS &&
    css`
      @media screen and (max-width: 768px) {
        padding-bottom: 12px;
      }
    `}
  div.slick-prev, div.slick-next {
    width: 44px;
    height: 44px;
    z-index: 1;
    background: rgba(0, 0, 0, 0.3);
    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
    & button {
      display: block;
      width: inherit;
      height: inherit;
      padding: 0;
      border: none;
      background: transparent;
      font-size: 0;
      line-height: 0;
      cursor: pointer;

      @media screen and (max-width: 768px) {
        svg {
          width: 5px;
        }
      }
    }
  }
  div.slick-prev {
    left: 0;
  }
  div.slick-next {
    right: 0;
  }
  ${layoutCustom(
    810,
    css`
      div.slick-prev {
        left: 0px;
      }
      div.slick-next {
        right: 0px;
      }
    `,
  )};
  ${layoutSmall(css`
    div.slick-prev,
    div.slick-next {
      width: 24px;
      height: 24px;
      img {
        width: 100%;
      }
    }
    div.slick-prev {
      left: 0px;
    }
    div.slick-next {
      right: 0px;
    }
  `)}
  div.slick-prev:before, div.slick-next:before {
    content: '';
  }
  div.slick-dots {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 40px;
    max-width: 750px;
    margin: 0 auto;
    text-align: left;
    ul {
      display: flex;
      align-items: center;
      padding-left: 40px;
      margin: 0;
      li {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 10px;
        height: 10px;
        margin: 0 10px 0 0;
        &.slick-active {
          width: 40px;
          .slider-dot {
            width: 40px;
            border-radius: 3px;
            opacity: 0.7;
          }
        }
        & button {
          width: inherit;
          height: inherit;
          padding: 0;
          background-color: transparent;
          border: none;
          font-size: 0;
          line-height: 0;
          cursor: pointer;
        }
      }
      div.slider-dot {
        width: 10px;
        height: 10px;
        background: #fff;
        border-radius: 50%;
        font-size: 0;
        line-height: 0;
        color: transparent;
        transition: all 0.2s;
      }
    }
  }
  ${layoutSmall(css`
    div.slick-dots {
      bottom: 12.5%;
      ul {
        padding-left: 25px;
        li {
          width: 5px;
          height: 5px;
          margin: 0 5px 0 0;
          &.slick-active {
            width: 20px;
            div.slider-dot {
              width: 20px;
            }
          }
        }
        div.slider-dot {
          width: 5px;
          height: 5px;
        }
      }
    }
  `)};
`
